/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import "./CallHistory.css";
import Panel from "../Panels/Panel";
import { camelToTitleCase } from "../../utility";
import { Spinner, Button, ListGroup, Badge } from "react-bootstrap";
import { format } from "date-fns";
import { BUILT_IN_TABS } from "../../../../utility/constants";
import { useContactData } from "../../context/contactData";

const CALLER_TYPES = {
	voice: "call",
	chat: "chat",
	task: "task",
};

const getEventListRenderContent = (events) => {
	return (
		<ListGroup>
			{events.map((event) => {
				return (
					<ListGroup.Item key={event.id} className="list-group-item px-3 py-2">
						<div className="d-flex w-100 align-items-center mb-2">
							<Badge variant="light" className="text-secondary p-2" style={{ fontSize: 12 }}>
								<span>{format(event.date, "pp")}</span>
								<span style={{ borderLeft: "1px solid #ccc" }} className="ml-2 pl-2">
									{camelToTitleCase(event.type)}
								</span>
							</Badge>
						</div>
						<span className="text-secondary">{event.text}</span>
					</ListGroup.Item>
				);
			})}
		</ListGroup>
	);
};

const RenderContent = ({ events, isLoading, error }) => {
	if (isLoading) {
		return (
			<Spinner animation="border" className="m-2" role="status">
				<span className="sr-only">Loading...</span>
			</Spinner>
		);
	}

	if (error) {
		return <span style={{ paddingLeft: 15 }}>{error}</span>;
	}

	if (!events || events.length === 0) {
		return <span style={{ paddingLeft: 15 }}>No events found.</span>;
	}

	return getEventListRenderContent(events);
};

const GetLayout = ({ isPanel, initialLoad, toggleExpand, events, caller, isLoading, error }) => {
	return isPanel ? (
		<Panel
			title={`${caller.type === "chat" ? "Chat" : "Call"} Path`}
			style={{ width: "100%", maxHeight: "400px" }}
			headerContent={
				<>
					{initialLoad && (
						<Button onClick={toggleExpand} size="sm" variant="outline-secondary">
							<i className="fas fa-sync"></i>
						</Button>
					)}
					<Button variant="link" onClick={toggleExpand} size="sm" className="ml-2 text-secondary">
						{initialLoad ? "Hide path" : "Show path"}
					</Button>
				</>
			}
		>
			{initialLoad && <RenderContent events={events} isLoading={isLoading} error={error} />}
		</Panel>
	) : (
		<div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
			<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, marginTop: 10 }}>
				<span style={{ color: "#6c757d", fontSize: 18 }}>
					List of contact flow events for the {CALLER_TYPES[caller.type] || "contact"}
				</span>
				{initialLoad ? (
					<Button onClick={toggleExpand} size="sm" variant="outline-secondary">
						<i className="fas fa-sync"></i> Refresh history
					</Button>
				) : (
					<Button onClick={toggleExpand} size="sm" variant="outline-secondary">
						<i className="fas fa-sync"></i> Load history
					</Button>
				)}
			</div>
			<div style={{ width: "100%" }}>
				{initialLoad && <RenderContent events={events} isLoading={isLoading} error={error} />}
			</div>
		</div>
	);
};

/**
 * This component retrieves and displays cloudwatch events for a caller based on their contactId.
 * @param {Object} caller This object contains information about the caller such as their contactId.
 */
export const CallHistory = ({ caller, isPanel, activeTab }) => {
	const { contactDataState, getContactHistory } = useContactData();
	const {
		loading = false,
		err = "",
		history = [],
		initialLoad = false,
	} = contactDataState[caller.contactId]?.history || {};
	React.useEffect(() => {
		if (activeTab === BUILT_IN_TABS.contact_history.id && !initialLoad) {
			getContactHistory(caller);
		}
	}, [activeTab, initialLoad, caller, getContactHistory]);

	const toggleExpand = () => {
		getContactHistory(caller);
	};

	return (
		<GetLayout
			isPanel={isPanel}
			initialLoad={initialLoad}
			toggleExpand={toggleExpand}
			events={history}
			caller={caller}
			isLoading={loading}
			error={err}
		/>
	);
};
